import { jwtDecode } from 'jwt-decode'
import { ChapterBackup } from '../types/editor'
import {
  getChapterBackupRepository,
  isProjectAvailableOffline as isProjectMarkedAvailableOffline,
  legacyLocalStorageKey,
  LEGACY_METADATA_KEY,
  touchOfflineProjectSync,
  type ChapterBackupRecord,
  type ChapterBackupWriteInput,
} from '../storage'
import { resolveSyncPhase } from '@/utils/chapterSyncPhase'

const CONFLICT_THRESHOLD = 5000 // 5 seconds

function getCurrentUserId(): string | undefined {
  try {
    const accessToken = localStorage.getItem('accessToken')
    if (!accessToken) return undefined
    const decoded = jwtDecode<{ userId?: string }>(accessToken)
    return decoded.userId || undefined
  } catch {
    return undefined
  }
}

export function isBackupOfferedToCurrentUser(backup: ChapterBackup): boolean {
  const currentUserId = getCurrentUserId()
  if (!currentUserId) {
    return false
  }
  if (backup.userId) {
    return backup.userId === currentUserId
  }
  // Legacy records without userId: visible to any logged-in session (accepted device risk).
  return true
}

function isRecordOwnedByAnotherUser(
  record: ChapterBackupRecord | null,
  currentUserId: string,
): boolean {
  if (!record?.userId) {
    return false
  }
  return record.userId !== currentUserId
}

function toChapterBackup(record: ChapterBackupRecord): ChapterBackup {
  return {
    title: record.title,
    content: record.content,
    jsonContent: record.jsonContent,
    timestamp: record.timestamp,
    projectId: record.projectId,
    chapterId: record.chapterId,
    savedAt: record.savedAt,
    sourceId: record.sourceId,
    userId: record.userId,
    baseUpdatedAt: record.baseUpdatedAt,
    baseContentVersion: record.baseContentVersion,
    inDoubtSaveIds: record.inDoubtSaveIds,
    pendingSync: record.pendingSync,
    syncPhase: record.syncPhase,
    lastSuccessfulSyncAt: record.lastSuccessfulSyncAt,
    lastErrorCode: record.lastErrorCode,
    lastErrorMessage: record.lastErrorMessage,
    retained: record.retained,
    order: record.order,
    patchScope: record.patchScope,
  }
}

/**
 * Thin facade over ChapterBackupRepository.
 * Keeps the historical sync BackupService API used by stores/composables,
 * and only offers a backup to the user who owns it.
 */
const BackupService = {
  generateSourceId(): string {
    return getChapterBackupRepository().getSourceId()
  },

  generateKey(projectId: string, chapterId: string): string {
    return getChapterBackupRepository().generateKey(projectId, chapterId)
  },

  isChapterBackupStorageKey(key: string): boolean {
    if (key === LEGACY_METADATA_KEY) {
      return true
    }
    try {
      const stored = localStorage.getItem(key)
      if (!stored) return false
      const backup = JSON.parse(stored) as ChapterBackup
      return (
        typeof backup?.projectId === 'string'
        && typeof backup?.chapterId === 'string'
        && key === legacyLocalStorageKey(backup.projectId, backup.chapterId)
      )
    } catch {
      return false
    }
  },

  saveDiscardedChapterSnapshot(
    backup: Omit<ChapterBackup, 'timestamp' | 'savedAt' | 'sourceId' | 'pendingSync' | 'retained'>,
  ): boolean {
    try {
      const userId = backup.userId || getCurrentUserId()
      if (!userId) {
        return false
      }

      getChapterBackupRepository().saveDiscardedSnapshot({
        projectId: backup.projectId,
        chapterId: backup.chapterId,
        title: backup.title || '',
        content: backup.content,
        jsonContent: backup.jsonContent || {},
        baseUpdatedAt: backup.baseUpdatedAt,
        order: backup.order,
        userId,
      })
      return true
    } catch (error) {
      console.error('Error saving discarded chapter snapshot:', error)
      return false
    }
  },

  saveServerConflictChapterSnapshot(
    backup: Omit<ChapterBackup, 'timestamp' | 'savedAt' | 'sourceId' | 'pendingSync' | 'retained'>,
  ): boolean {
    try {
      const userId = backup.userId || getCurrentUserId()
      if (!userId) {
        return false
      }

      getChapterBackupRepository().saveServerConflictSnapshot({
        projectId: backup.projectId,
        chapterId: backup.chapterId,
        title: backup.title || '',
        content: backup.content,
        jsonContent: backup.jsonContent || {},
        baseUpdatedAt: backup.baseUpdatedAt,
        baseContentVersion: backup.baseContentVersion,
        order: backup.order,
        userId,
      })
      return true
    } catch (error) {
      console.error('Error saving server conflict chapter snapshot:', error)
      return false
    }
  },

  saveChapterBackup(
    backup: Omit<ChapterBackup, 'timestamp' | 'savedAt' | 'sourceId'>,
  ): boolean {
    try {
      const userId = backup.userId || getCurrentUserId()
      if (!userId) {
        return false
      }

      const existing = getChapterBackupRepository().get(backup.projectId, backup.chapterId)
      if (isRecordOwnedByAnotherUser(existing, userId)) {
        return false
      }

      const input: ChapterBackupWriteInput = {
        projectId: backup.projectId,
        chapterId: backup.chapterId,
        title: backup.title || '',
        content: backup.content,
        jsonContent: backup.jsonContent || {},
        baseUpdatedAt: backup.baseUpdatedAt,
        baseContentVersion: backup.baseContentVersion,
        inDoubtSaveIds: backup.inDoubtSaveIds,
        pendingSync: backup.pendingSync,
        syncPhase: backup.syncPhase,
        lastSuccessfulSyncAt: backup.lastSuccessfulSyncAt,
        lastErrorCode: backup.lastErrorCode,
        lastErrorMessage: backup.lastErrorMessage,
        retained: backup.retained,
        order: backup.order,
        patchScope: backup.patchScope,
        userId,
      }
      getChapterBackupRepository().save(input)
      return true
    } catch (error) {
      console.error('Error saving chapter backup:', error)
      return false
    }
  },

  getChapterBackup(projectId: string, chapterId: string): ChapterBackup | null {
    try {
      const record = getChapterBackupRepository().get(projectId, chapterId)
      return this.offerBackupToCurrentUser(record ? toChapterBackup(record) : null)
    } catch (error) {
      console.error('Error getting chapter backup:', error)
      return null
    }
  },

  offerBackupToCurrentUser(backup: ChapterBackup | null): ChapterBackup | null {
    if (!backup) return null
    if (!isBackupOfferedToCurrentUser(backup)) {
      return null
    }
    return backup
  },

  retagBackupsForUser(fromUserId: string, toUserId: string): void {
    if (!fromUserId || !toUserId || fromUserId === toUserId) {
      return
    }

    try {
      Object.keys(localStorage).forEach((key) => {
        if (key === LEGACY_METADATA_KEY || !this.isChapterBackupStorageKey(key)) {
          return
        }
        const stored = localStorage.getItem(key)
        if (!stored) return
        const backup = JSON.parse(stored) as ChapterBackup
        if (backup.userId === fromUserId) {
          backup.userId = toUserId
          localStorage.setItem(key, JSON.stringify(backup))
        }
      })
      getChapterBackupRepository().retagUser(fromUserId, toUserId)
    } catch (error) {
      console.error('Error retagging chapter backups:', error)
    }
  },

  /** Resolve a backup when only the chapter id is known. */
  findChapterBackup(chapterId: string): ChapterBackup | null {
    try {
      const record = getChapterBackupRepository().findByChapterId(chapterId)
      return this.offerBackupToCurrentUser(record ? toChapterBackup(record) : null)
    } catch (error) {
      console.error('Error finding chapter backup:', error)
      return null
    }
  },

  isServerMoreRecent(localBackup: ChapterBackup, serverTimestamp: number): boolean {
    const timeDiff = Math.abs(localBackup.timestamp - serverTimestamp)

    // If timestamps are within threshold, consider them simultaneous and prefer server
    if (timeDiff < CONFLICT_THRESHOLD) {
      return false
    }

    return serverTimestamp > localBackup.timestamp
  },

  /**
   * Persist a clean server mirror. Cleanup will not delete it.
   * Single-chapter saves refresh the offline project timestamp.
   * Bulk downloads pass touchProjectSync: false and mark the project once at the end.
   */
  saveSyncedChapterCopy(
    backup: Omit<ChapterBackup, 'timestamp' | 'savedAt' | 'sourceId' | 'pendingSync' | 'retained'>,
    options?: { touchProjectSync?: boolean },
  ): boolean {
    const saved = this.saveChapterBackup({
      ...backup,
      pendingSync: false,
      retained: true,
      syncPhase: 'offline_mirror',
      lastSuccessfulSyncAt: Date.now(),
    })
    if (!saved) return false
    if (options?.touchProjectSync === false) return true

    touchOfflineProjectSync(backup.projectId, {
      lastSyncedAt: new Date().toISOString(),
      lastServerChangeAt: backup.baseUpdatedAt,
    })
    return true
  },

  isProjectAvailableOffline(projectId: string): boolean {
    return isProjectMarkedAvailableOffline(projectId)
  },

  patchChapterBackup(
    projectId: string,
    chapterId: string,
    updates: Partial<ChapterBackupRecord> & { bumpTimestamp?: boolean },
  ): ChapterBackup | null {
    try {
      const existing = getChapterBackupRepository().get(projectId, chapterId)
      const existingBackup = existing ? toChapterBackup(existing) : null
      if (existingBackup && !isBackupOfferedToCurrentUser(existingBackup)) {
        return null
      }
      const record = getChapterBackupRepository().patch(projectId, chapterId, updates)
      return this.offerBackupToCurrentUser(record ? toChapterBackup(record) : null)
    } catch (error) {
      console.error('Error patching chapter backup:', error)
      return null
    }
  },

  removeChapterBackup(projectId: string, chapterId: string): void {
    try {
      const record = getChapterBackupRepository().get(projectId, chapterId)
      const backup = record ? toChapterBackup(record) : null
      if (backup && !isBackupOfferedToCurrentUser(backup)) {
        return
      }
      getChapterBackupRepository().remove(projectId, chapterId)
    } catch (error) {
      console.error('Error removing chapter backup:', error)
    }
  },

  cleanupProjectBackups(projectId: string): void {
    try {
      getChapterBackupRepository().cleanupProject(projectId)
    } catch (error) {
      console.error('Error cleaning up project backups:', error)
    }
  },

  isStorageAvailable(): boolean {
    return getChapterBackupRepository().isStorageAvailable()
  },

  listChapterBackups(): ChapterBackup[] {
    try {
      return getChapterBackupRepository()
        .listAll()
        .map(toChapterBackup)
        .filter((backup) => isBackupOfferedToCurrentUser(backup))
    } catch (error) {
      console.error('Error listing chapter backups:', error)
      return []
    }
  },

  getAllBackupKeys(): string[] {
    try {
      return getChapterBackupRepository().getAllBackupKeys()
    } catch (error) {
      console.error('Error getting backup keys:', error)
      return []
    }
  },

  initialize(): void {
    try {
      getChapterBackupRepository().initialize()
    } catch (error) {
      console.error('Error initializing chapter backup service:', error)
    }
  },

  whenReady(): Promise<void> {
    try {
      return getChapterBackupRepository().whenReady()
    } catch (error) {
      console.error('Error waiting for chapter backup service:', error)
      return Promise.resolve()
    }
  },

  async clearAllChapterBackups(): Promise<void> {
    try {
      await getChapterBackupRepository().clearAll()
    } catch (error) {
      console.error('Error clearing chapter backups:', error)
    }
  },

  /**
   * Manual logout: drop synced mirrors for this user and orphan legacy rows; keep pending uploads.
   */
  pruneChapterBackupsOnManualLogout(userId: string): void {
    if (!userId) return
    try {
      for (const record of getChapterBackupRepository().listAll()) {
        if (!record.userId) {
          getChapterBackupRepository().remove(record.projectId, record.chapterId)
          continue
        }
        if (record.userId !== userId) continue
        const phase = resolveSyncPhase(record)
        if (phase === 'synced_grace' || phase === 'offline_mirror') {
          getChapterBackupRepository().remove(record.projectId, record.chapterId)
        }
      }
      if (typeof localStorage === 'undefined') return
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i)
        if (!key || !this.isChapterBackupStorageKey(key)) continue
        try {
          const stored = localStorage.getItem(key)
          if (!stored) continue
          const legacy = JSON.parse(stored) as ChapterBackup
          if (!legacy.userId) {
            localStorage.removeItem(key)
          }
        } catch {
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      console.error('Error pruning chapter backups on logout:', error)
    }
  },
}

export default BackupService
